// ─────────────────────────────────────────────────────────────────────────────
//  utils/index.jsx
//  Primitive UI building blocks shared across all Challan Settlement sections.
//  Each atom is a pure, presentational component with no internal state.
//  Export them from one file to keep import lines tidy in consumers.
// ─────────────────────────────────────────────────────────────────────────────

import { S, sectionThemes } from "../styles/ChallanSettlementStyles";

// ── Badge ──────────────────────────────────────────────────────────────────

/**
 * Small pill badge.
 * @param {{ variant?: "gray"|"amber"|"red"|"green", onDark?: boolean, children: React.ReactNode }} props
 */
export const Badge = ({ variant = "gray", onDark = false, children }) => (
    <span style={S.badge(variant, onDark)}>{children}</span>
);

// ── BadgeGroup ─────────────────────────────────────────────────────────────

/**
 * Wrapper that spaces multiple badges horizontally.
 * @param {{ children: React.ReactNode }} props
 */
export const BadgeGroup = ({ children }) => (
    <div style={S.badgeGroup}>{children}</div>
);

// ── Flag ───────────────────────────────────────────────────────────────────

/**
 * Inline alert banner with a coloured dot.
 * @param {{ variant?: "info"|"warn"|"err"|"ok", dotColor?: string, children: React.ReactNode }} props
 */
export const Flag = ({ variant = "info", dotColor, children }) => (
    <div style={S.flag(variant)}>
        <div style={S.flagDot(dotColor)} />
        <div style={S.flagText}>{children}</div>
    </div>
);

// ── InfoCard ───────────────────────────────────────────────────────────────

/**
 * Labelled value tile used in header grids and summary columns.
 * @param {{ label: string, value: React.ReactNode, sub?: string, valueStyle?: React.CSSProperties }} props
 */
export const InfoCard = ({ label, value, sub, valueStyle = {} }) => (
    <div style={S.iCard} className="cs-icard">
        <div style={S.iCardLabel}>{label}</div>
        <div style={{ ...S.iCardValue, ...valueStyle }}>{value}</div>
        {sub && <div style={S.iCardSub}>{sub}</div>}
    </div>
);

// ── ProgressBar ────────────────────────────────────────────────────────────

/**
 * Thin horizontal progress bar.
 * @param {{ pct: number, color: string }} props
 */
export const ProgressBar = ({ pct, color }) => (
    <div style={S.progBg}>
        <div style={S.progFill(pct, color)} />
    </div>
);

// ── SectionCard ────────────────────────────────────────────────────────────

/**
 * Themed card wrapper used by every Settlement section.
 * `themeKey` drives the header gradient/colour via `sectionThemes`.
 *
 * @param {{
 *   themeKey: string,
 *   icon: React.ReactNode,
 *   title: string,
 *   badge?: React.ReactNode,
 *   children: React.ReactNode
 * }} props
 */
export const SectionCard = ({ themeKey, icon, title, badge, children }) => {
    const theme = sectionThemes[themeKey] ?? null;

    return (
        <div style={S.section} className="cs-section">
            <div style={S.sectionHeader(theme)} className="cs-section-header">
                <div style={S.sectionTitle(theme)}>
                    <div style={S.sectionIconWrap(theme)}>{icon}</div>
                    {title}
                </div>
                {badge}
            </div>
            {children}
        </div>
    );
};